import React from 'react';
import { Box, Button, Dialog, Typography } from '@mui/material';
import { isPackPromoActive, FEATURED_ARTISTS } from '../data/artistContent';
import { isInternalBuild } from '../config/distribution';
import { needsAccountBeforePurchase } from '../lib/purchaseGate';

// Dialogue d'achat d'un pack. Sorti de Learn en S9 : deux gestes d'achat s'y
// jouent, et ils n'obeissaient a aucune des regles que le reste de l'app
// applique a un achat.
//
//  - le pack lui-meme, qui n'existe aujourd'hui qu'en maquette (aucun produit
//    RevenueCat par pack, un build public refuse l'achat) ;
//  - "unlock everything", qui achete l'app entiere depuis ici. Celui-la est
//    reel et public, et il contournait la garde du compte (F3) parce qu'elle
//    vivait dans UnlockSheet.
//
// Les deux passent maintenant par la meme porte : compte avant paiement sur le
// web, proposition de compte apres l'achat comme partout ailleurs (famille F1).
//
// Une divergence assumee avec UnlockSheet : ce dialogue se FERME avant
// d'envoyer poser un compte, alors que le verrou reste ouvert derriere sa
// feuille. C'est un Dialog MUI, la feuille de compte s'ouvrirait derriere lui.
// La page du pack, elle, reste a l'ecran : on re-clique.

const cancelSx = {
  py: 1.2,
  borderRadius: '10px',
  backgroundColor: 'rgba(255,255,255,0.08)',
  color: '#fff',
  textTransform: 'none',
  fontSize: '1rem',
  whiteSpace: 'nowrap',
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
};

const buySx = {
  py: 1.2,
  borderRadius: '10px',
  backgroundColor: 'var(--button)',
  color: 'var(--button-text)',
  fontWeight: 'bold',
  textTransform: 'none',
  fontSize: '1rem',
  whiteSpace: 'nowrap',
  '&:hover': { backgroundColor: 'var(--button-hover)' },
};

const PackPurchaseDialog = ({
  pack,
  signedIn,
  priceLabel,
  realPayments,
  trackCount = 0,
  purchasePack,
  unlockFullApp,
  onClose,
  onPurchased,
  onRequestSignUp,
}) => {
  // Le compte d'abord, quel que soit le geste : c'est la meme caisse.
  const sendToAccount = () => {
    onClose?.();
    onRequestSignUp?.('before_purchase');
  };

  // Apres un achat, la proposition de compte sert a l'emporter ailleurs. Elle
  // est conditionnee ici (et pas laissee a la feuille, qui se ferme seule quand
  // une session existe) pour que "pas de proposition si deja connecte" se lise
  // dans ce fichier.
  const afterPurchase = () => {
    onPurchased?.(pack);
    if (!signedIn) onRequestSignUp?.('purchase');
  };

  const handleConfirmPurchase = async () => {
    if (needsAccountBeforePurchase({ signedIn })) return sendToAccount();
    const price = isPackPromoActive(pack) ? pack.promoPrice : pack.price;
    const res = await purchasePack?.(pack.id, price);
    if (res?.success) afterPurchase();
    else onClose?.();
  };

  const handleUnlockEverything = async () => {
    if (needsAccountBeforePurchase({ signedIn })) return sendToAccount();
    const res = await unlockFullApp?.({ from: 'pack_dialog' });
    // Annulation ou echec : on laisse le dialog ouvert, l'utilisateur retente.
    if (res?.success) afterPurchase();
  };

  const promoActive = pack ? isPackPromoActive(pack) : false;

  return (
    <Dialog
      open={!!pack}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: '#15151A',
          backgroundImage: 'none',
          borderRadius: '14px',
          border: '1px solid rgba(255,255,255,0.08)',
          p: 3,
          maxWidth: 340,
        },
      }}
    >
      {pack && (
        <Box sx={{ textAlign: 'center' }}>
          {pack.bundled ? (
            <>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'white', mb: 1 }}>
                Unlock the app
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', mb: 2.5 }}>
                {pack.name} is included with the app. Unlocking also opens every key and every scale in Play, plus the core backings.
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'white', mb: 1 }}>
                Unlock {pack.name} for {promoActive ? pack.promoPrice : pack.price}
              </Typography>
              {promoActive && (
                <Typography sx={{ fontSize: '0.8rem', color: '#10b981', mb: 1 }}>
                  Promo price (was <span style={{ textDecoration: 'line-through' }}>{pack.price}</span>) until {new Date(pack.promoUntil).toLocaleDateString()}
                </Typography>
              )}
              <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', mb: 2.5 }}>
                Includes {trackCount} backing tracks by {FEATURED_ARTISTS[pack.artistId]?.name}.
              </Typography>
            </>
          )}
          {/* Sans paiement reel : mock en build interne, achat indisponible en
              build public. Prevenir avant le clic plutot que d'echouer apres. */}
          {!realPayments && (
            <Box sx={{
              backgroundColor: isInternalBuild() ? 'rgba(16,185,129,0.15)' : 'rgba(249,115,22,0.15)',
              borderRadius: '10px',
              p: 1.5,
              mb: 2.5
            }}>
              <Typography sx={{ fontSize: '0.8rem', color: isInternalBuild() ? '#10b981' : '#f97316' }}>
                {isInternalBuild()
                  ? 'Dev build: purchases are simulated, nothing is charged.'
                  : 'Purchases are unavailable right now. Please check your connection and try again.'}
              </Typography>
            </Box>
          )}
          {pack.bundled ? (
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button fullWidth onClick={onClose} sx={cancelSx}>
                Cancel
              </Button>
              <Button fullWidth onClick={handleUnlockEverything} sx={buySx}>
                Unlock · {priceLabel}
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button fullWidth onClick={onClose} sx={cancelSx}>
                  Cancel
                </Button>
                <Button fullWidth onClick={handleConfirmPurchase} sx={buySx}>
                  Unlock
                </Button>
              </Box>
              <Button
                fullWidth
                onClick={handleUnlockEverything}
                sx={{
                  mt: 1.5,
                  py: 1,
                  textTransform: 'none',
                  fontSize: '1rem',
                  color: 'rgba(255,255,255,0.75)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' }
                }}
              >
                Or unlock everything · {priceLabel}
              </Button>
            </>
          )}
        </Box>
      )}
    </Dialog>
  );
};

export default PackPurchaseDialog;
